import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close, FlagOutlined, Send as SendIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiService, ErpSyncAuditResponse, Product, StoreProductOverride } from '../../services/api';
import type { ErpResyncOptions } from '../../services/api';
import { BrandMark } from '../productBrowser/BrandMark';
import { ProductFilterMark } from '../../utils/productBrowserFilterState';
import { hydrateProductVariants, NormalizedProductVariant } from '../../utils/productVariants';
import { getEffectiveMapForProduct } from '../../utils/mapPricing';
import { liveStorefrontPrice } from '../../utils/erpSyncLiveListing';
import { validSpecKeys } from '../../utils/specFormatting';
import { tokens } from '../../theme/tokens';
import { lifecycleStageOf } from '../../utils/productBrowserFilterState';
import {
  CameraWithPricing,
  BrowserActionFeedback,
  OverrideFormState,
  EMPTY_OVERRIDE_FORM,
  ProductPriceHistoryRow,
  formatDate,
  formatDateInput,
  buildOverridePayload,
  buildAIDescriptionPayload,
  toListingPricingData,
  getMarketPricingRefreshUi,
} from '../../utils/productDetailHelpers';
import { ProductOverviewTab } from './ProductOverviewTab';
import type { ProductOverviewTabProps } from './ProductOverviewTab';
import { ProductSpecificationsTab } from './ProductSpecificationsTab';
import { ProductDetailsMergedTab, detailsContentCount } from './ProductDetailsMergedTab';
import { NeutralNote } from './NeutralNote';
import { DealerOverviewTab } from './dealerView/DealerOverviewTab';
import { DealerStoreTab, formsEqual } from './dealerView/DealerStoreTab';
import { useUnsavedChangesSource } from '../appUpdate/unsavedChanges';
import { useStoreRelatedPicks } from './useStoreRelatedPicks';
import { useStorePreorderGoLive } from './useStorePreorderGoLive';
import { DealerPricingTab } from './dealerView/DealerPricingTab';
import { DealerCompatibilityTab, compatibilityCounts } from './dealerView/DealerCompatibilityTab';
import { DealerSpecsTab, DealerDetailsTab, dealerDetailsCount } from './dealerView/DealerSpecsDetailsTabs';
import { buildListingModel, catalogAdvertisedPrice, shortDay, validateOverride } from './dealerView/dealerModel';
import { dealerPlanLockApplies, shouldShowDealerProductView } from './dealerView/dealerViewGate';
import { LockedFeature } from '../planLock/LockedFeature';
import { usePlanSurface } from '../planLock/usePlanSurface';
import { buildPricingWindows, mapFloorFor } from './dealerView/pricingWindows';
import type { CompatibilityItem } from './ProductWorksWith';
import VariantGroupSelectAll from '../productBrowser/VariantGroupSelectAll';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import {
  STORE_AI_COPY_RULES_KEY,
  acceptStoreAiCopy,
  discardStoreAiCopy,
  fetchStoreAiCopyRules,
  generateStoreAiCopy,
} from '../../services/storeAiCopyApi';
import type { ProductOverridesAiCopy, ProductOverridesAiSpecs } from './ProductOverridesSection';
import { applyAiSpecSuggestion, dismissAiSpecSuggestion } from '../../services/aiBulkApi';
import type { StoreSpecsEditorProps } from './StoreSpecsEditor';
import { useStoreOwnSpecsSave } from './useStoreOwnSpecsSave';
import { useAppTranslation } from '../../i18n/useAppTranslation';

type DialogTab = 'overview' | 'store' | 'pricing' | 'compatibility' | 'specifications' | 'details';

interface ProductDetailsDialogProps {
  open: boolean;
  product: CameraWithPricing | null;
  onClose: () => void;
  onVariantClick: (variant: NormalizedProductVariant) => void;
  onLinkedProductClick: (product: Product) => void;
  variantSwitchLoadingSku: string | null;
  onOpenGallery: (images: string[], title: string, index: number) => void;
  onFeedback: (feedback: BrowserActionFeedback) => void;
  brandMark: ProductFilterMark;
  syncSelectedIds: Set<string>;
  /** The integration whose selection the browser is showing; null for the store's own. */
  selectionIntegrationId?: string | null;
  isStoreScoped: boolean;
  isPlatformAdmin: boolean;
  isVendor: boolean;
  canManageStoreOverrides: boolean;
  /**
   * Owner or admin of the acting store, or a platform admin impersonating one
   * (flag `store_ai_copy`): the AI copy routes refuse everyone else, so a
   * plain member sees the block read-only.
   */
  canManageStoreAiCopy?: boolean;
  showStoreOverridesPanel: boolean;
  allowBelowMap: boolean;
  onAddToSync: () => void;
  addToSyncPending: boolean;
  onRemoveFromSync: () => void;
  removeFromSyncPending: boolean;
  onViewSyncSelection: () => void;
  /**
   * Adds other catalog products to the sync selection (the dealer view's
   * Compatibility tab). Resolves when the selection is saved.
   */
  onAddProductsToSync?: (productIds: string[]) => Promise<unknown>;
}

/** The store's saved override as the form edits it. */
const overrideFormFrom = (o: StoreProductOverride | null | undefined): OverrideFormState => ({
  overrideTitle: o?.overrideTitle || '',
  overrideDescription: o?.overrideDescription || '',
  overrideTags: Array.isArray(o?.overrideTags) ? o!.overrideTags!.join(', ') : '',
  overridePrice: o?.overridePrice != null ? String(o.overridePrice) : '',
  overridePriceStartAt: formatDateInput(o?.overridePriceStartAt),
  overridePriceEndAt: formatDateInput(o?.overridePriceEndAt),
  ignoreSalePrice: Boolean(o?.ignoreSalePrice),
  ignoreRebate: Boolean(o?.ignoreRebate),
});

const TABS: Array<{ value: DialogTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'specifications', label: 'Specifications' },
  { value: 'details', label: 'Details' },
];

/** The dealer view (flag `dealer_product_view`, plan `dealerProductView`) splits Overview into store, pricing and compatibility tabs. */
const DEALER_TABS: Array<{ value: DialogTab; label: string }> = [
  { value: 'overview', label: 'Overview' },
  { value: 'store', label: 'Your store' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'compatibility', label: 'Compatibility' },
  { value: 'specifications', label: 'Specifications' },
  { value: 'details', label: 'Details' },
];

const money = (n: number | null | undefined): string =>
  n != null && Number.isFinite(Number(n))
    ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

export function ProductDetailsDialog({
  open,
  product: seedProduct,
  onClose,
  onVariantClick,
  onLinkedProductClick,
  variantSwitchLoadingSku,
  onOpenGallery,
  onFeedback,
  brandMark,
  syncSelectedIds,
  selectionIntegrationId,
  isStoreScoped,
  isPlatformAdmin,
  isVendor,
  canManageStoreOverrides,
  canManageStoreAiCopy = false,
  showStoreOverridesPanel,
  allowBelowMap,
  onAddToSync,
  addToSyncPending,
  onRemoveFromSync,
  removeFromSyncPending,
  onViewSyncSelection,
  onAddProductsToSync,
}: ProductDetailsDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useAppTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [product, setProduct] = useState<CameraWithPricing | null>(seedProduct);
  const [activeTab, setActiveTab] = useState<DialogTab>('overview');
  const [storeOverridesExpanded, setStoreOverridesExpanded] = useState(false);
  const [overrideForm, setOverrideForm] = useState<OverrideFormState>(EMPTY_OVERRIDE_FORM);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState('incorrect_price');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSuggestedValue, setReportSuggestedValue] = useState('');

  // Switching products (variant/linked-product click) resets tab-scoped UI
  // state, matching how the dialog behaved before the split. Skipped while
  // closed: the page nulls its selection on close, and clearing `product`
  // there would unmount the dialog's content mid exit-transition. Keying on
  // `open` still resets the tab when the same product is reopened.
  useEffect(() => {
    if (!open || !seedProduct) return;
    setProduct(seedProduct);
    setActiveTab('overview');
    setStoreOverridesExpanded(false);
  }, [open, seedProduct?.id]);

  const productId = product?.id;

  const { data: fullProductData } = useQuery({
    queryKey: ['product-detail', productId],
    queryFn: () => apiService.getCameraById(productId!),
    enabled: open && !!productId,
    staleTime: 60000,
    refetchInterval: (query) => {
      const ui = getMarketPricingRefreshUi(query.state.data?.marketPricing);
      return ui.state === 'refreshing' ? 4000 : false;
    },
  });
  useEffect(() => {
    if (fullProductData) {
      setProduct((prev) => {
        if (!prev || fullProductData.id !== prev.id) return prev;
        const nextSpecs = fullProductData.specifications;
        const keepPrevSpecs =
          (!nextSpecs || typeof nextSpecs !== 'object' || Object.keys(nextSpecs).every((key) => key.startsWith('_')))
          && prev.specifications
          && typeof prev.specifications === 'object'
          && Object.keys(prev.specifications).some((key) => !key.startsWith('_'));
        return {
          ...prev,
          ...fullProductData,
          pricingData: prev.pricingData,
          ...(keepPrevSpecs ? { specifications: prev.specifications } : {}),
        };
      });
    }
  }, [fullProductData]);

  const { data: linkedVariants = [], isLoading: linkedVariantsLoading } = useQuery({
    queryKey: ['product-linked-variants', productId],
    queryFn: () => apiService.getLinkedProductVariants(productId!),
    enabled: open && !!productId,
    staleTime: 60000,
  });

  const displayVariants = useMemo(
    () => hydrateProductVariants(product?.variants, product?.specifications),
    [product?.variants, product?.specifications],
  );

  const { data: productPriceHistory = [] } = useQuery<ProductPriceHistoryRow[]>({
    queryKey: ['product-price-history', productId, product?.model, product?.brand],
    queryFn: () => apiService.getPricingHistory(product!.brand, product!.model, 25, productId!),
    enabled: open && !!productId,
    staleTime: 60000,
  });

  const pricingModelQuery = useQuery({
    queryKey: ['pricing-model', productId, product?.model, product?.sku, product?.mpn, product?.brand],
    queryFn: () => apiService.getPricingByModel(product!.model || product!.sku!, productId, product!.brand, product!.sku, product!.mpn),
    enabled: open && !!productId && !!(product?.model || product?.sku),
    staleTime: 5 * 60 * 1000,
  });
  useEffect(() => {
    const pricingData = toListingPricingData(pricingModelQuery.data);
    if (!pricingData || !productId) return;
    setProduct((prev) => (prev && prev.id === productId ? { ...prev, pricingData } : prev));
  }, [pricingModelQuery.data, productId]);

  const { data: storeProductOverride, isLoading: overrideLoading } = useQuery({
    queryKey: ['store-product-override', productId],
    queryFn: () => apiService.getStoreProductOverride(productId!),
    enabled: open && canManageStoreOverrides && !!productId,
  });
  // A refetch (e.g. after an AI copy action) only replaces the fields whose
  // server value changed, so unsaved edits in the other fields survive.
  const lastOverrideSnapshotRef = useRef<{ productId: string; form: OverrideFormState } | null>(null);
  useEffect(() => {
    if (!showStoreOverridesPanel || !product) {
      lastOverrideSnapshotRef.current = null;
      return;
    }
    if (!canManageStoreOverrides) {
      lastOverrideSnapshotRef.current = null;
      setOverrideForm(EMPTY_OVERRIDE_FORM);
      return;
    }
    const next = overrideFormFrom(storeProductOverride);
    const previous = lastOverrideSnapshotRef.current;
    lastOverrideSnapshotRef.current = productId ? { productId, form: next } : null;
    if (!previous || previous.productId !== productId) {
      setOverrideForm(next);
      return;
    }
    setOverrideForm((current) => {
      const merged = { ...current };
      (Object.keys(next) as Array<keyof OverrideFormState>).forEach((key) => {
        if (next[key] !== previous.form[key]) (merged as Record<string, unknown>)[key] = next[key];
      });
      return merged;
    });
  }, [showStoreOverridesPanel, canManageStoreOverrides, productId, storeProductOverride]);

  // Read-only: shares the erp-sync-audit cache with ErpSyncAuditPanel (same
  // query key) so the blocked-sync alert up top and the sync panel below
  // never disagree about the latest push.
  const { data: auditData } = useQuery<ErpSyncAuditResponse>({
    queryKey: ['erp-sync-audit', productId],
    queryFn: () => apiService.getErpSyncAudit(productId!),
    enabled: open && !!productId && isStoreScoped,
    staleTime: 30000,
  });

  const forceResyncMutation = useMutation({
    mutationFn: (opts: ErpResyncOptions = {}) => apiService.forceErpResync(productId!, undefined, opts),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['erp-sync-audit'] });
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      queryClient.invalidateQueries({ queryKey: ['sync-selection'] });
      const results = result.results ?? [];
      const first = results[0];
      const label = first?.platform === 'bigcommerce' ? 'BigCommerce' : first?.platform === 'lightspeed' ? 'Lightspeed' : first?.platform === 'shopify' ? 'Shopify' : 'Store';
      const warning = results
        .flatMap((row) => row.warnings ?? [])
        .map((row) => row.message)
        .filter(Boolean)
        .join(' ');
      const failed = results.find((row) => row.status === 'error');
      if (first?.status === 'success' && !failed) {
        const base = `${label} listing was updated for this product.`;
        onFeedback(warning ? { severity: 'warning', message: `${base} ${warning}` } : { severity: 'success', message: base });
      } else if (failed) {
        onFeedback({ severity: 'error', message: failed.error || warning || `${label} resync failed.` });
      } else if (first?.status === 'skipped') {
        const priceMiss = first.warnings?.find((w) => w.code === 'shopify_price_not_written');
        onFeedback({
          severity: 'info',
          message: first.error || priceMiss?.message || warning || `${label} skipped this product.`,
        });
      } else {
        onFeedback({ severity: 'error', message: first?.error || warning || `${label} resync failed.` });
      }
    },
    onError: (err: Error) => onFeedback({ severity: 'error', message: err.message || 'Failed to resync product to the store.' }),
  });

  const refreshMarketPricingMutation = useMutation({
    mutationFn: () => apiService.refreshProductMarketPricing(productId!),
    onMutate: () => {
      const refreshingAt = new Date().toISOString();
      if (productId) {
        queryClient.setQueryData(['product-detail', productId], (prev: Product | null | undefined) =>
          prev ? { ...prev, marketPricing: { ...prev.marketPricing, refreshingAt } } : prev
        );
      }
      setProduct((prev) => (prev ? { ...prev, marketPricing: { ...prev.marketPricing, refreshingAt } } : prev));
    },
    onSuccess: (result) => {
      if (result?.id) {
        queryClient.setQueryData(['product-detail', result.id], result);
        setProduct((prev) => (prev && prev.id === result.id ? { ...prev, ...result } : prev));
      } else {
        void queryClient.invalidateQueries({ queryKey: ['product-detail', productId] });
      }
      onFeedback({ severity: 'success', message: 'Market pricing updated.' });
    },
    onError: (error: any) => {
      void queryClient.invalidateQueries({ queryKey: ['product-detail', productId] });
      const data = error?.response?.data;
      const days = data?.nextRefreshInDays;
      const apiMessage = data?.error || data?.message || error?.message;
      const message =
        data?.code === 'MARKET_PRICING_COOLDOWN' && typeof days === 'number'
          ? `Next refresh in ${days} day${days === 1 ? '' : 's'}`
          : data?.code === 'MARKET_PRICING_IN_PROGRESS'
            ? 'A refresh is already running for this product.'
            : apiMessage || 'Failed to refresh market pricing.';
      onFeedback({ severity: 'error', message });
    },
  });

  const saveOverrideMutation = useMutation({
    mutationFn: (payload: Partial<StoreProductOverride>) => apiService.upsertStoreProductOverride(productId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-product-override', productId] });
      onFeedback({ severity: 'success', message: 'Store overrides saved.' });
    },
    onError: () => onFeedback({ severity: 'error', message: 'Failed to save store overrides.' }),
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: () => apiService.deleteStoreProductOverride(productId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-product-override', productId] });
      setOverrideForm(EMPTY_OVERRIDE_FORM);
      onFeedback({ severity: 'success', message: 'Store overrides cleared.' });
    },
    onError: () => onFeedback({ severity: 'error', message: 'Failed to clear store overrides.' }),
  });

  // The store's own AI copy (flag `store_ai_copy`, MOT-153): a draft on the
  // override document, reviewed here; nothing pushes until it is accepted.
  const { enabled: storeAiCopyEnabled } = useOrgFeatureFlag('store_ai_copy');
  const showStoreAiCopy = storeAiCopyEnabled && showStoreOverridesPanel && canManageStoreOverrides;
  const [aiCopyError, setAiCopyError] = useState<string | null>(null);
  const aiCopyRulesQuery = useQuery({
    queryKey: STORE_AI_COPY_RULES_KEY,
    queryFn: () => fetchStoreAiCopyRules('Could not load your copy rules.'),
    enabled: open && showStoreAiCopy,
    staleTime: 60000,
  });
  useEffect(() => {
    setAiCopyError(null);
  }, [productId]);
  const invalidateAiCopy = () => {
    queryClient.invalidateQueries({ queryKey: ['store-product-override', productId] });
    queryClient.invalidateQueries({ queryKey: STORE_AI_COPY_RULES_KEY });
  };
  const generateAiCopyMutation = useMutation({
    mutationFn: () => generateStoreAiCopy(productId!, 'Could not generate copy for this product.'),
    onSuccess: (data) => {
      setAiCopyError(null);
      invalidateAiCopy();
      onFeedback({ severity: 'success', message: data.cached ? 'Draft restored from your last generation; nothing was spent.' : 'Draft ready for your review.' });
    },
    onError: (error: Error) => setAiCopyError(error.message),
  });
  const acceptAiCopyMutation = useMutation({
    mutationFn: (fields?: Array<'title' | 'description'>) => acceptStoreAiCopy(productId!, fields, 'Could not accept the draft.'),
    onSuccess: () => {
      setAiCopyError(null);
      invalidateAiCopy();
      onFeedback({ severity: 'success', message: 'Copy accepted. It pushes on the next sync.' });
    },
    onError: (error: Error) => onFeedback({ severity: 'error', message: error.message }),
  });
  const discardAiCopyMutation = useMutation({
    mutationFn: () => discardStoreAiCopy(productId!, 'Could not discard the draft.'),
    onSuccess: () => {
      setAiCopyError(null);
      invalidateAiCopy();
    },
    onError: (error: Error) => onFeedback({ severity: 'error', message: error.message }),
  });
  const aiCopy: ProductOverridesAiCopy | null = showStoreAiCopy
    ? {
        canManage: canManageStoreAiCopy,
        draft: storeProductOverride?.aiCopyDraft ?? null,
        usage: aiCopyRulesQuery.data?.usage ?? null,
        planIncluded: aiCopyRulesQuery.data?.planIncluded ?? true,
        onGenerate: () => generateAiCopyMutation.mutate(),
        generatePending: generateAiCopyMutation.isPending,
        onAccept: (fields) => acceptAiCopyMutation.mutate(fields),
        acceptPending: acceptAiCopyMutation.isPending,
        onDiscard: () => discardAiCopyMutation.mutate(),
        discardPending: discardAiCopyMutation.isPending,
        error: aiCopyError,
      }
    : null;

  // Approved bulk-AI spec suggestions (flag `bulk_ai_operations`, MOT-400):
  // shown beside the AI copy block; Use / Dismiss write through the bulk-AI
  // routes (owner or admin), never to the catalog.
  const { enabled: bulkAiEnabled } = useOrgFeatureFlag('bulk_ai_operations');
  const [specPendingLabel, setSpecPendingLabel] = useState<string | null>(null);
  const specSuggestions = storeProductOverride?.aiSpecSuggestions ?? {};
  const usedSpecs = storeProductOverride?.overrideSpecs ?? {};
  const specMutation = useMutation({
    mutationFn: ({ action, label }: { action: 'use' | 'dismiss'; label: string }) => {
      setSpecPendingLabel(label);
      const fallback = t('store.aiSpecs.error');
      return action === 'use' ? applyAiSpecSuggestion(productId!, label, fallback) : dismissAiSpecSuggestion(productId!, label, fallback);
    },
    onSuccess: (_data, { action, label }) => {
      queryClient.invalidateQueries({ queryKey: ['store-product-override', productId] });
      onFeedback({ severity: 'success', message: action === 'use' ? t('store.aiSpecs.used', { label }) : t('store.aiSpecs.dismissed') });
    },
    onError: (error: Error) => onFeedback({ severity: 'error', message: error.message }),
    onSettled: () => setSpecPendingLabel(null),
  });
  // The store's own spec values, editable here (flag `store_override_specs_push`, MOT-413). With the
  // editor shown the read-only "Specs you use" list is left out so the values appear once.
  const { enabled: ownSpecsEnabled } = useOrgFeatureFlag('store_override_specs_push');
  const showOwnSpecs = ownSpecsEnabled && showStoreOverridesPanel && canManageStoreOverrides;
  const ownSpecsMutation = useStoreOwnSpecsSave(productId, {
    onSaved: () => onFeedback({ severity: 'success', message: t('store.ownSpecs.saved') }),
    onError: () => onFeedback({ severity: 'error', message: t('store.ownSpecs.error') }),
  });
  // The labels the push matches (normalized catalog labels), so a picked spec reaches its mapping.
  const { data: ownSpecLabels } = useQuery({
    queryKey: ['store-product-spec-labels', productId],
    queryFn: () => apiService.getStoreProductSpecLabels(productId!),
    enabled: open && showOwnSpecs && !!productId,
    staleTime: 5 * 60 * 1000,
  });
  const ownSpecs: StoreSpecsEditorProps | null = showOwnSpecs
    ? {
        specs: usedSpecs,
        labelOptions: ownSpecLabels ?? [],
        onSave: (next) => ownSpecsMutation.mutate(next),
        pending: ownSpecsMutation.isPending,
      }
    : null;
  const aiSpecsUsed = showOwnSpecs ? {} : usedSpecs;
  const aiSpecs: ProductOverridesAiSpecs | null =
    bulkAiEnabled && showStoreOverridesPanel && canManageStoreOverrides && (Object.keys(specSuggestions).length > 0 || Object.keys(aiSpecsUsed).length > 0)
      ? {
          suggestions: specSuggestions,
          used: aiSpecsUsed,
          canManage: canManageStoreAiCopy,
          onUse: (label) => specMutation.mutate({ action: 'use', label }),
          onDismiss: (label) => specMutation.mutate({ action: 'dismiss', label }),
          pendingLabel: specPendingLabel,
        }
      : null;

  const generateDescriptionMutation = useMutation({
    mutationFn: () => apiService.generateProductDescription(buildAIDescriptionPayload(product!)),
    onSuccess: (data) => {
      if (data?.description) setOverrideForm((prev) => ({ ...prev, overrideDescription: data.description }));
      onFeedback({ severity: 'success', message: data?.isAI ? 'AI description generated.' : 'Description generated with fallback copy.' });
    },
    onError: () => onFeedback({ severity: 'error', message: 'Failed to generate AI description.' }),
  });

  const reviewDiscountMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => {
      const review = product?.pricing?.suspiciousDiscountReview;
      return apiService.reviewSuspiciousDiscount(productId!, decision, { advertisedPrice: review?.advertisedPrice, floor: review?.floor });
    },
    onSuccess: (_data, decision) => {
      void queryClient.invalidateQueries({ queryKey: ['product-detail', productId] });
      void queryClient.invalidateQueries({ queryKey: ['product-price-history', productId] });
      setProduct((prev) => {
        if (!prev?.pricing?.suspiciousDiscountReview) return prev;
        return {
          ...prev,
          pricing: { ...prev.pricing, suspiciousDiscountReview: { ...prev.pricing.suspiciousDiscountReview, status: decision, reviewedAt: new Date().toISOString() } },
        };
      });
      onFeedback({
        severity: 'success',
        message: decision === 'approved' ? 'Price approved. The next sync will push it normally.' : 'Price rejected. It is recorded in price change history and will not sync.',
      });
    },
    onError: (err: Error) => onFeedback({ severity: 'error', message: err.message || 'Failed to save discount review.' }),
  });

  const closeReport = () => {
    setReportOpen(false);
    setReportCategory('incorrect_price');
    setReportDescription('');
    setReportSuggestedValue('');
  };

  const submitReportMutation = useMutation({
    mutationFn: () =>
      apiService.submitProductIssue({
        productId: product!.id,
        productSku: product!.sku || '',
        productName: product!.name || '',
        productBrand: product!.brand || '',
        category: reportCategory,
        description: reportDescription,
        suggestedValue: reportSuggestedValue || undefined,
      }),
    onSuccess: () => {
      closeReport();
      onFeedback({ severity: 'success', message: 'Issue reported. Thank you for helping improve our data!' });
    },
    onError: (error: any) => {
      const apiMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      onFeedback({ severity: 'error', message: apiMessage ? `Failed to submit report: ${apiMessage}` : 'Failed to submit report. Please try again.' });
    },
  });

  const inSync = product ? syncSelectedIds.has(product.id) : false;

  // Dealer view follows the acting store's flag and plan, including a
  // platform admin impersonating that store; a platform admin outside
  // impersonation always gets it, vendors never (MOT-459).
  const { enabled: dealerViewFlag } = useOrgFeatureFlag('dealer_product_view');
  const dealerSurface = usePlanSurface({ feature: 'dealerProductView', orgFlag: 'dealer_product_view' });
  const { enabled: lensCompatEnabled } = useOrgFeatureFlag('lens_compatibility');
  // Flag `retailer_related_products` (MOT-705): the store's own picks, held here so unsaved picks stay
  // guarded while another tab is open. A store session only; null with the flag off.
  const { enabled: relatedPicksFlag } = useOrgFeatureFlag('retailer_related_products');
  const relatedPicks = useStoreRelatedPicks(product?.id, open && isStoreScoped && relatedPicksFlag);
  // Flag `store_preorder_go_live` (MOT-766): held here for the same reason as the picks.
  const { enabled: preorderGoLiveFlag } = useOrgFeatureFlag('store_preorder_go_live');
  const preorderGoLive = useStorePreorderGoLive(product?.id, open && isStoreScoped && preorderGoLiveFlag);
  // The plan lock is a store-session notice. A vendor never gets this view, and a
  // platform admin outside impersonation always does (MOT-459).
  const dealerPlanLock = dealerPlanLockApplies({ isStoreScoped, isVendor, isPlatformAdmin });
  const dealerView = dealerPlanLock && (dealerSurface.mode === 'locked' || dealerSurface.hold)
    ? false
    : shouldShowDealerProductView({
        flagEnabled: dealerViewFlag,
        isStoreScoped,
        isVendor,
        isPlatformAdmin,
      });
  const storefrontPrice = liveStorefrontPrice(auditData);
  const catalogPrice = product ? catalogAdvertisedPrice(product) : null;
  const listing = buildListingModel(auditData, inSync, catalogPrice);
  const savedOverrideForm = overrideFormFrom(storeProductOverride);
  // Registered here, not in the Your store tab, so unsaved overrides stay guarded while another tab is open.
  const overridesDirty = open && dealerView && canManageStoreOverrides && !!product && !formsEqual(overrideForm, savedOverrideForm);
  const overridesBlocked = product ? validateOverride(product, overrideForm, allowBelowMap).blocked : true;
  useUnsavedChangesSource({
    dirty: overridesDirty,
    // Rejecting keeps a forced refresh waiting while the override can't be saved, instead of reloading over it.
    save: () =>
      overridesBlocked || !product
        ? Promise.reject(new Error('Fix the store override price or dates before saving.'))
        : saveOverrideMutation.mutateAsync(buildOverridePayload(overrideForm, product)),
    discard: () => setOverrideForm(savedOverrideForm),
  });
  const overrideCount = [
    storeProductOverride?.overrideTitle,
    storeProductOverride?.overrideDescription,
    storeProductOverride?.overrideTags?.length ? 'tags' : null,
    storeProductOverride?.overridePrice != null ? 'price' : null,
    storeProductOverride?.ignoreSalePrice ? 'sale' : null,
    storeProductOverride?.ignoreRebate ? 'rebate' : null,
  ].filter(Boolean).length;
  const mapFloor = product ? mapFloorFor(product) : null;
  const overrideBelowFloor =
    mapFloor != null && storeProductOverride?.overridePrice != null && Number(storeProductOverride.overridePrice) < mapFloor - 0.005;
  const liveWindows = product ? buildPricingWindows(product).filter((w) => w.kind !== 'map' && w.status === 'live') : [];
  const promoLive = liveWindows.length > 0;
  const compatCounts = lensCompatEnabled ? compatibilityCounts(product?.compatibility, t) : null;
  const compatTotal = compatCounts?.reduce((n, g) => n + g.count, 0) ?? 0;
  // "Your store" needs a store: an admin outside impersonation has none to show.
  const dealerTabs = DEALER_TABS.filter((tab) =>
    tab.value === 'compatibility' ? !!compatCounts : tab.value === 'store' ? isStoreScoped : true,
  );

  // Classification "Report to Motzu": one product issue per product, remembered for this session.
  const [reportedClassification, setReportedClassification] = useState<Set<string>>(new Set());
  const reportClassificationMutation = useMutation({
    mutationFn: (issue: string) =>
      apiService.submitProductIssue({
        productId: product!.id,
        productSku: product!.sku || '',
        productName: product!.name || '',
        productBrand: product!.brand || '',
        category: 'other',
        description: `Classification disagrees: ${issue}`,
      }),
    onSuccess: () => {
      if (product) setReportedClassification((prev) => new Set(prev).add(product.id));
      onFeedback({ severity: 'success', message: 'Thanks. The catalog team will fix the category.' });
    },
    onError: () => onFeedback({ severity: 'error', message: 'Could not send the report. Try again.' }),
  });

  // Compatibility "+ Add to sync".
  const [addingCompatIds, setAddingCompatIds] = useState<Set<string>>(new Set());
  const addCompatibleToSync = async (item: CompatibilityItem) => {
    if (!onAddProductsToSync) return;
    setAddingCompatIds((prev) => new Set(prev).add(item.id));
    try {
      // The page's add-to-sync mutation reports success and failure (including the plan cap) itself.
      await onAddProductsToSync([item.id]);
    } catch {
      /* reported by the page */
    } finally {
      setAddingCompatIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };
  const em = product ? getEffectiveMapForProduct(product) : null;
  const specCount = product ? validSpecKeys(product.specifications).length : 0;
  const detailCount = product ? (dealerView ? dealerDetailsCount(product) : detailsContentCount(product)) : 0;
  // In the dealer view the description lives on Details, so copy alone keeps the tab.
  const hasCopy = Boolean(product?.shortDescription || product?.description);

  const headChips: Array<{ label: string; bg: string; color: string; dot?: string }> = [];
  if (product) {
    // Pre-release stage (flag `preorder_lifecycle`): first, so a dealer sees it is not on sale yet.
    // The row carries `lifecycle` only where the browser shows the lifecycle chip.
    const lifecycleStage = lifecycleStageOf(product);
    if (lifecycleStage !== 'released') {
      const tone = lifecycleStage === 'preorder' ? tokens.signal.caution : tokens.signal.info;
      const ships = product.lifecycle?.releaseDate ? formatDate(product.lifecycle.releaseDate) : null;
      headChips.push({
        label: `${t(`productLifecycle.stage.${lifecycleStage}`)}${ships ? ` · ${t('productLifecycle.ships', { date: ships })}` : ''}`,
        bg: tone.tint,
        color: tone.ink,
        dot: tone.solid,
      });
    }
    if (em?.raw) headChips.push({ label: 'MAP available', bg: tokens.signal.positive.tint, color: tokens.signal.positive.ink, dot: tokens.signal.positive.solid });
    if (product.manufacturerStock?.status && product.manufacturerStock.status !== 'unknown') {
      // Colour the chip by the actual stock status — a green chip on an
      // out-of-stock or discontinued product reads as "in stock" at a glance.
      const stockStatus = product.manufacturerStock.status;
      const stockTone =
        stockStatus === 'out_of_stock' || stockStatus === 'discontinued'
          ? { bg: tokens.signal.problem.tint, color: tokens.signal.problem.ink, dot: tokens.signal.problem.solid }
          : stockStatus === 'backorder' || stockStatus === 'preorder'
            ? { bg: tokens.signal.caution.tint, color: tokens.signal.caution.ink, dot: tokens.signal.caution.solid }
            : { bg: tokens.signal.positive.tint, color: tokens.signal.positive.ink, dot: tokens.signal.positive.solid };
      headChips.push({
        label: `${product.brand}: ${product.manufacturerStock.label || stockStatus.replace(/_/g, ' ')}`,
        ...stockTone,
      });
    }
    if (product.pricing?.missingAdvertisedPriceReview?.status === 'pending') {
      headChips.push({ label: 'No advertised price', bg: tokens.signal.caution.tint, color: tokens.signal.caution.ink, dot: tokens.signal.caution.solid });
    }
    if (dealerView) {
      const rebateWindow = liveWindows.find((w) => w.kind === 'rebate');
      if (rebateWindow) {
        headChips.push({
          label: `Instant rebate${rebateWindow.endMs != null ? ` · ends ${shortDay(rebateWindow.endMs)}` : ''}`,
          bg: tokens.signal.info.tint,
          color: tokens.signal.info.ink,
          dot: tokens.signal.info.solid,
        });
      }
      if (product.isProOnly) headChips.push({ label: 'Pro only', bg: tokens.signal.caution.tint, color: tokens.signal.caution.ink, dot: tokens.signal.caution.solid });
    } else if (isStoreScoped) {
      headChips.push(
        inSync
          ? { label: 'In sync', bg: tokens.signal.info.tint, color: tokens.signal.info.ink }
          : { label: 'Not in sync', bg: tokens.surface.tint, color: 'text.secondary' }
      );
    }
  }

  // Blocked-sync alert: only when the MOST RECENT audit event is itself a
  // skip on a MAP violation — a later created/updated event means the
  // block has already been resolved and no banner is shown.
  const latestEvent = auditData?.events?.length
    ? [...auditData.events].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0]
    : null;
  const blockedAlert = latestEvent && latestEvent.status === 'skipped' && latestEvent.skipCode === 'below_map' ? latestEvent : null;
  const missingPriceAlert =
    product?.pricing?.missingAdvertisedPriceReview?.status === 'pending' ||
    (latestEvent?.status === 'skipped' && latestEvent.skipCode === 'no_valid_price');
  const blockedFalsePositive = (() => {
    if (!blockedAlert) return false;
    const map = blockedAlert.catalog?.map;
    const pushedPrice = blockedAlert.pushed?.price;
    if (map == null || pushedPrice == null) return false;
    // The bug signature is narrow: advertised below RAW map but at or above
    // MAP - IR, i.e. the old guard compared against raw MAP while a rebate was
    // running. A push at raw MAP with a rebate merely sitting on the catalog
    // snapshot (upcoming/expired) is a legitimate skip, not a false positive.
    // The snapshot records the rebate amount but not its window, so this can
    // only say the skip *looks* like the known false positive — the copy below
    // is conditional on the rebate having been live at push time.
    const ir = Number(blockedAlert.catalog?.instantRebate) || 0;
    const effectiveFloor = ir > 0 ? map - ir : map;
    return pushedPrice < map - 0.01 && pushedPrice >= effectiveFloor - 0.01;
  })();

  const buildOverviewProps = (product: CameraWithPricing): ProductOverviewTabProps => ({
    product: product,
    displayVariants: displayVariants,
    linkedVariants: linkedVariants,
    linkedVariantsLoading: linkedVariantsLoading,
    variantSwitchLoadingSku: variantSwitchLoadingSku,
    onVariantClick: onVariantClick,
    onLinkedProductClick: onLinkedProductClick,
    onOpenCompatibleProduct: (item) =>
                    onLinkedProductClick({
                      id: item.id,
                      name: item.name,
                      model: item.name,
                      brand: item.brand,
                      images: item.image ? [item.image] : [],
                    } as unknown as Product)
                  ,
    productPriceHistory: productPriceHistory,
    isStoreScoped: isStoreScoped,
    isVendor: isVendor,
    isPlatformAdmin: isPlatformAdmin,
    inSync: inSync,
    onAddToSync: onAddToSync,
    addToSyncPending: addToSyncPending,
    onForceResync: (opts) => forceResyncMutation.mutate(opts ?? {}),
    forceResyncPending: forceResyncMutation.isPending,
    onRemoveFromSync: onRemoveFromSync,
    removeFromSyncPending: removeFromSyncPending,
    onViewSyncSelection: onViewSyncSelection,
    onOpenGallery: onOpenGallery,
    onReviewDiscount: (decision) => reviewDiscountMutation.mutate(decision),
    reviewPending: reviewDiscountMutation.isPending,
    onRefreshMarketPricing: () => refreshMarketPricingMutation.mutate(),
    refreshMarketPricingPending: refreshMarketPricingMutation.isPending,
    showStoreOverridesPanel: showStoreOverridesPanel,
    overridesExpanded: storeOverridesExpanded,
    onToggleOverridesExpanded: () => setStoreOverridesExpanded((prev) => !prev),
    overrideForm: overrideForm,
    onOverrideFormChange: setOverrideForm,
    canManageStoreOverrides: canManageStoreOverrides,
    overrideLoading: overrideLoading,
    onSaveOverrides: () => saveOverrideMutation.mutate(buildOverridePayload(overrideForm, product)),
    savePending: saveOverrideMutation.isPending,
    onClearOverrides: () => deleteOverrideMutation.mutate(),
    clearPending: deleteOverrideMutation.isPending,
    onGenerateDescription: () => generateDescriptionMutation.mutate(),
    generatePending: generateDescriptionMutation.isPending,
    allowBelowMap: allowBelowMap,
    storefrontPrice: liveStorefrontPrice(auditData),
    aiCopy: aiCopy,
    aiSpecs: aiSpecs,
    ownSpecs: ownSpecs,
    relatedPicks: relatedPicks,
    preorderGoLive: preorderGoLive,
  });

  return (
    <>
      {/* The Dialog shell stays mounted regardless of `product` so MUI can
          play its normal open/close transition — only the content inside
          is gated, matching how the pre-split dialog behaved. */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '14px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: isMobile ? '100%' : 'calc(100% - 32px)',
            m: isMobile ? 0 : undefined,
          },
        }}
      >
        {product && (
          <>
            {dealerPlanLock && dealerSurface.mode === 'locked' && (
              <Box sx={{ px: 2, pt: 2 }}>
                <LockedFeature variant="inline" planName={dealerSurface.planName} />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: { xs: 2, sm: '22px' },
                pt: isMobile ? 'max(12px, env(safe-area-inset-top))' : '18px',
                flexShrink: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, minWidth: 0 }}>
                  <BrandMark mark={brandMark} size={isMobile ? 22 : 28} fontSize={isMobile ? 11 : 12} wide />
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontSize: '11.5px',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                    }}
                  >
                    {product.brand}
                  </Typography>
                  <Box sx={{ display: { xs: 'none', sm: 'block' }, width: 3, height: 3, borderRadius: '50%', bgcolor: tokens.ink.disabled, flexShrink: 0 }} />
                  <Typography
                    noWrap
                    sx={{ fontSize: { xs: '12px', sm: '12px' }, color: tokens.ink.faint, fontVariantNumeric: 'tabular-nums', minWidth: 0 }}
                  >
                    SKU {product.sku}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: { xs: '17px', sm: '19px' }, fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em', color: 'text.primary' }}>
                  {product.name || product.model}
                </Typography>
                {headChips.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    {headChips.map((chip) => (
                      <Box
                        key={chip.label}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, height: 22, px: '8px', borderRadius: '6px', bgcolor: chip.bg, color: chip.color, fontSize: '11.5px', fontWeight: 600 }}
                      >
                        {chip.dot && <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: chip.dot }} />}
                        {chip.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title="Report issue">
                  <Button
                    onClick={() => setReportOpen(true)}
                    startIcon={<FlagOutlined sx={{ fontSize: 16 }} />}
                    aria-label="Report issue"
                    sx={{
                      minWidth: { xs: 40, sm: 'auto' },
                      width: { xs: 40, sm: 'auto' },
                      height: { xs: 40, sm: dealerView ? 34 : 32 },
                      px: { xs: 0, sm: dealerView ? '12px' : '10px' },
                      borderRadius: '9px',
                      border: '1px solid',
                      borderColor: tokens.signal.caution.border,
                      bgcolor: dealerView ? tokens.signal.caution.row : 'background.paper',
                      color: tokens.signal.caution.ink,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: dealerView ? '13px' : '12.5px',
                      '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.6 }, ml: { xs: 0, sm: -0.25 } },
                      '&:hover': { borderColor: tokens.signal.caution.solid, bgcolor: tokens.signal.caution.tint, color: tokens.signal.caution.ink },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      {dealerView ? 'Report issue' : 'Report Issue'}
                    </Box>
                  </Button>
                </Tooltip>
                <IconButton
                  onClick={onClose}
                  aria-label="Close product details"
                  sx={{ width: { xs: 40, sm: dealerView ? 34 : 32 }, height: { xs: 40, sm: dealerView ? 34 : 32 }, borderRadius: '9px' }}
                >
                  <Close sx={{ fontSize: { xs: 18, sm: 13 } }} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 0.25,
                px: { xs: 1, sm: '22px' },
                pt: { xs: 1, sm: '14px' },
                borderBottom: '1px solid',
                borderColor: 'divider',
                overflowX: 'auto',
                flexShrink: 0,
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {(dealerView ? dealerTabs : TABS).map((tab) => {
                const on = activeTab === tab.value;
                const count =
                  tab.value === 'specifications' ? specCount
                    : tab.value === 'details' ? detailCount
                      : tab.value === 'compatibility' ? compatTotal
                        : null;
                const dot = !dealerView
                  ? null
                  : tab.value === 'store' && (listing.state === 'price_differs' || overrideBelowFloor)
                    ? tokens.signal.caution.solid
                    : tab.value === 'pricing' && promoLive
                      ? tokens.signal.positive.solid
                      : null;
                if (tab.value === 'details' && detailCount === 0 && !(dealerView && hasCopy)) return null;
                return (
                  <Box
                    key={tab.value}
                    component="button"
                    onClick={() => setActiveTab(tab.value)}
                    sx={{
                      appearance: 'none', bgcolor: 'transparent', border: 'none', borderBottom: '2px solid', borderColor: on ? 'primary.main' : 'transparent',
                      px: { xs: '12px', sm: '12px' }, py: { xs: '12px', sm: '9px' }, display: 'flex', alignItems: 'center', gap: 0.9, cursor: 'pointer',
                      color: on ? 'text.primary' : 'text.secondary', fontSize: '13.5px', fontWeight: on ? 600 : 500, font: 'inherit',
                      flexShrink: 0, whiteSpace: 'nowrap', minHeight: 44,
                    }}
                  >
                    <span>{tab.label}</span>
                    {count != null && !(dealerView && count === 0) && (
                      <Typography component="span" sx={{ fontSize: '12px', color: on ? 'primary.main' : tokens.ink.faint, fontVariantNumeric: 'tabular-nums' }}>
                        {count}
                      </Typography>
                    )}
                    {dot && <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot }} />}
                  </Box>
                );
              })}
            </Box>

            {isStoreScoped && !dealerView && product.variantGroupId && (
              <Box sx={{ px: { xs: '16px', sm: '22px' }, py: 1, '&:empty': { display: 'none' } }}>
                <VariantGroupSelectAll variant="button" productId={product.id} variantGroupId={product.variantGroupId} integrationId={selectionIntegrationId} selectedIds={syncSelectedIds} />
              </Box>
            )}

            {(product.isArchived || blockedAlert || missingPriceAlert) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
                {product.isArchived && (
                  <Box sx={{ p: { xs: '13px 16px', sm: '13px 22px' } }}>
                    <NeutralNote>
                      <strong>This product has been archived.</strong>{product.archivedReason ? ` ${product.archivedReason}` : ''}
                    </NeutralNote>
                  </Box>
                )}
                {missingPriceAlert && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4, p: { xs: '13px 16px', sm: '13px 22px' }, bgcolor: tokens.signal.caution.tint, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
                        Last push was skipped — no advertised price
                      </Typography>
                      <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.secondary', mt: 0.3 }}>
                        {isPlatformAdmin
                          ? 'Motzu has no retail, MAP, or MSRP to send to the storefront, so this SKU was skipped instead of pushing $0. Add manufacturer pricing to clear the flag.'
                          : 'Motzu has no retail, MAP, or MSRP for this SKU, so it was not pushed. Motzu admins have been flagged. You can set a store price override if you still want it listed.'}
                      </Typography>
                    </Box>
                    {isStoreScoped && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                        <Button
                          onClick={() => {
                            setActiveTab(dealerView ? 'store' : 'overview');
                            setStoreOverridesExpanded(true);
                          }}
                          sx={{ height: 30, px: '12px', borderRadius: '8px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', textTransform: 'none', fontWeight: 600, fontSize: '12.5px' }}
                        >
                          Set store price
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
                {blockedAlert && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.4, p: { xs: '13px 16px', sm: '13px 22px' }, bgcolor: tokens.surface.sunken, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: 'text.primary' }}>
                        Last push was skipped — price is below MAP
                      </Typography>
                      <Typography sx={{ fontSize: '12.5px', lineHeight: 1.55, color: 'text.secondary', mt: 0.3 }}>
                        {blockedFalsePositive
                          ? `The guard compared ${money(blockedAlert.pushed?.price)} against raw MAP ${money(blockedAlert.catalog?.map)}, and the snapshot recorded a ${money(blockedAlert.catalog?.instantRebate)} rebate. If that rebate was live at push time this price was compliant, so the skip is worth re-checking — the snapshot doesn't record the rebate window.`
                          : blockedAlert.error || `Advertised ${money(blockedAlert.pushed?.price)} is below MAP ${money(blockedAlert.catalog?.map)}. Nothing has been pushed to the storefront since.`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
                      {/* Store-scoped only: this mutation resyncs without an
                          integrationId, so an admin who is not impersonating a
                          store would push against no particular mapping. Admins
                          keep using the sync panel's own Force resync until the
                          two are reconciled. */}
                      {isStoreScoped && (
                        <Button
                          onClick={() => forceResyncMutation.mutate({})}
                          disabled={forceResyncMutation.isPending}
                          sx={{ height: 30, px: '12px', borderRadius: '8px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', textTransform: 'none', fontWeight: 600, fontSize: '12.5px' }}
                        >
                          {forceResyncMutation.isPending ? <CircularProgress size={14} /> : 'Retry push'}
                        </Button>
                      )}
                      {isVendor && (
                        <Button
                          onClick={() => navigate('/app/vendor-map-management')}
                          sx={{ height: 30, px: '12px', color: 'text.secondary', textTransform: 'none', fontWeight: 600, fontSize: '12.5px' }}
                        >
                          Review MAP
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            <DialogContent sx={{ p: 0, flex: 1, overflow: 'auto', minHeight: 0, pb: { xs: 'env(safe-area-inset-bottom)', sm: 0 } }}>
              {activeTab === 'overview' && !dealerView && <ProductOverviewTab {...buildOverviewProps(product)} />}
              {dealerView && activeTab === 'overview' && (
                <DealerOverviewTab
                  product={product}
                  history={productPriceHistory}
                  displayVariants={displayVariants}
                  linkedVariants={linkedVariants}
                  linkedVariantsLoading={linkedVariantsLoading}
                  variantSwitchLoadingSku={variantSwitchLoadingSku}
                  onVariantClick={onVariantClick}
                  onLinkedProductClick={onLinkedProductClick}
                  onOpenGallery={onOpenGallery}
                  storefrontPrice={storefrontPrice}
                  listing={listing}
                  overrideCount={overrideCount}
                  inSync={inSync}
                  syncSelectedIds={syncSelectedIds}
                  selectionIntegrationId={selectionIntegrationId}
                  compatCounts={compatCounts}
                  onOpenStore={() => setActiveTab('store')}
                  onOpenPricing={() => setActiveTab('pricing')}
                  onOpenCompatibility={() => setActiveTab('compatibility')}
                  onReportClassification={(issue) => reportClassificationMutation.mutate(issue)}
                  classificationReported={reportedClassification.has(product.id)}
                  classificationReportPending={reportClassificationMutation.isPending}
                  onReviewDiscount={(decision) => reviewDiscountMutation.mutate(decision)}
                  reviewPending={reviewDiscountMutation.isPending}
                  hasStore={isStoreScoped}
                  isPlatformAdmin={isPlatformAdmin}
                />
              )}
              {dealerView && isStoreScoped && activeTab === 'store' && (
                <DealerStoreTab
                  product={product}
                  audit={auditData}
                  listing={listing}
                  inSync={inSync}
                  onAddToSync={onAddToSync}
                  addToSyncPending={addToSyncPending}
                  onPush={(opts) => forceResyncMutation.mutate(opts)}
                  pushPending={forceResyncMutation.isPending}
                  onRemoveFromSync={onRemoveFromSync}
                  removeFromSyncPending={removeFromSyncPending}
                  onViewSyncSelection={onViewSyncSelection}
                  form={overrideForm}
                  savedForm={savedOverrideForm}
                  onFormChange={setOverrideForm}
                  canManageStoreOverrides={canManageStoreOverrides}
                  overrideLoading={overrideLoading}
                  onSaveOverrides={() => saveOverrideMutation.mutateAsync(buildOverridePayload(overrideForm, product))}
                  savePending={saveOverrideMutation.isPending}
                  onGenerateDescription={() => generateDescriptionMutation.mutate()}
                  generatePending={generateDescriptionMutation.isPending}
                  allowBelowMap={allowBelowMap}
                  aiCopy={aiCopy}
                  aiSpecs={aiSpecs}
                  ownSpecs={ownSpecs}
                  relatedPicks={relatedPicks}
                  preorderGoLive={preorderGoLive}
                  onOpenRelatedProduct={(item) =>
                    onLinkedProductClick({ id: item.id, name: item.name, model: item.name, brand: item.brand, images: item.image ? [item.image] : [] } as unknown as Product)
                  }
                />
              )}
              {dealerView && activeTab === 'pricing' && (
                <DealerPricingTab
                  product={product}
                  isStoreScoped={isStoreScoped}
                  onRefreshMarketPricing={() => refreshMarketPricingMutation.mutate()}
                  refreshMarketPricingPending={refreshMarketPricingMutation.isPending}
                />
              )}
              {dealerView && activeTab === 'compatibility' && product.compatibility && (
                <DealerCompatibilityTab
                  productId={product.id}
                  compatibility={product.compatibility}
                  syncSelectedIds={syncSelectedIds}
                  canAddToSync={!!onAddProductsToSync}
                  onAddToSync={(item) => void addCompatibleToSync(item)}
                  addingIds={addingCompatIds}
                  onOpenProduct={(item) =>
                    onLinkedProductClick({ id: item.id, name: item.name, model: item.name, brand: item.brand, images: item.image ? [item.image] : [] } as unknown as Product)
                  }
                />
              )}
              {dealerView && activeTab === 'specifications' && (
                <DealerSpecsTab product={product} onCopied={(n) => onFeedback({ severity: 'success', message: `Copied ${n} specifications` })} />
              )}
              {dealerView && activeTab === 'details' && <DealerDetailsTab product={product} />}
              {!dealerView && activeTab === 'specifications' && (
                <Box sx={{ p: { xs: 2, sm: '22px' } }}>
                  <ProductSpecificationsTab product={product} />
                </Box>
              )}
              {!dealerView && activeTab === 'details' && (
                <Box sx={{ p: { xs: 2, sm: '22px' } }}>
                  <ProductDetailsMergedTab product={product} />
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>

      {product && (
        <Dialog open={reportOpen} onClose={closeReport} maxWidth="sm" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Report Issue — {product.name || 'Product'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Issue Category</InputLabel>
                <Select value={reportCategory} label="Issue Category" onChange={(e) => setReportCategory(e.target.value)}>
                  <MenuItem value="incorrect_price">Incorrect Price</MenuItem>
                  <MenuItem value="incorrect_name">Incorrect Name</MenuItem>
                  <MenuItem value="incorrect_description">Incorrect Description</MenuItem>
                  <MenuItem value="incorrect_specs">Incorrect Specifications</MenuItem>
                  <MenuItem value="wrong_image">Wrong Image</MenuItem>
                  <MenuItem value="missing_data">Missing Data</MenuItem>
                  <MenuItem value="duplicate_product">Duplicate Product</MenuItem>
                  <MenuItem value="discontinued">Should Be Discontinued</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="What's wrong?"
                multiline
                rows={3}
                fullWidth
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Describe the issue you've found with this product listing..."
                required
              />
              <TextField
                label="Suggested correction (optional)"
                fullWidth
                size="small"
                value={reportSuggestedValue}
                onChange={(e) => setReportSuggestedValue(e.target.value)}
                placeholder="e.g. correct price is $1,299.99"
              />
              <Typography variant="caption" color="text.disabled">
                Your report will be reviewed by the Motzu team. You'll be able to track its status from your account.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeReport}>Cancel</Button>
            <Button
              variant="contained"
              color="warning"
              startIcon={submitReportMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={() => submitReportMutation.mutate()}
              disabled={!reportDescription.trim() || submitReportMutation.isPending}
            >
              {submitReportMutation.isPending ? 'Submitting...' : 'Submit Report'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
